import { format, parse } from 'date-fns';

export type ArrivalTime = {
	hour: string;
	minute: string;
	ampm: string;
	display: string;
};

/**
 * Parses a default check-in time string (e.g., "3:00 PM") into hour, minute, and ampm parts
 * @param timeString - Time string in format like "3:00 PM"
 * @returns Object with hour, minute, and ampm, or null if parsing fails
 */
export function parseDefaultCheckinTime(
	timeString?: string
): { hour: string; minute: string; ampm: string } | null {
	if (!timeString) return null;

	// Parse format like "3:00 PM"
	const match = timeString.match(/(\d+):(\d+)\s*(AM|PM)/i);
	if (match) {
		return {
			hour: match[1],
			minute: match[2],
			ampm: match[3].toUpperCase(),
		};
	}
	return null;
}

/**
 * Formats a time string into an ArrivalTime object with display format
 * Handles various time formats including ISO strings and AM/PM formats
 * @param time - Time string to format
 * @returns ArrivalTime object or undefined if parsing fails
 */
export function formatArrivalTime(time?: string): ArrivalTime | undefined {
	if (!time) return undefined;

	let timeFormat = 'HH:mm:ss.SSS';
	let timeToParse = time;

	// Handle ISO format with milliseconds (e.g., "2024-01-01T15:30:00.000")
	const isoMatch = time.match(/(?<=T)\d{2}:\d{2}:\d{2}\.\d{3}/)?.[0];
	if (isoMatch) {
		timeToParse = isoMatch;
	} else if (/(A|P)M/gi.test(time)) {
		// Handle AM/PM format (e.g., "3:30 PM")
		timeFormat = 'h:mm a';
		timeToParse = time;
	}

	try {
		const timeParsed = parse(timeToParse, timeFormat, new Date());
		return {
			display: format(timeParsed, 'h:mm a'),
			hour: format(timeParsed, 'h'),
			minute: format(timeParsed, 'mm'),
			ampm: format(timeParsed, 'a').toUpperCase(),
		};
	} catch (error) {
		console.error('Error parsing time:', time, error);
		return undefined;
	}
}

/**
 * Converts time components to ISO format string
 * @param date - Date string in YYYY-MM-DD format
 * @param time - Object containing hour, minutes, and period
 * @returns ISO format string (YYYY-MM-DDTHH:mm:ss)
 */
export function toIsoFormat({
	date,
	time,
}: {
	date: string;
	time: { hour: number; minutes: number; period: 'am' | 'pm' };
}): string {
	const { hour, minutes, period } = time;
	let parsedHour: string;

	// Convert 12 to its equivalent value for am pm hour
	if (hour === 12) {
		parsedHour = period.toLowerCase() === 'am' ? '00' : '12';
	} else {
		// If am is selected set the selected hour if not set the selected hour + 12
		parsedHour = period.toLowerCase() === 'am' ? hour.toString() : (hour + 12).toString();
	}

	return `${date}T${parsedHour.padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
}

/**
 * Converts time components to ISO format string (alternative implementation)
 * @param hour - Hour string (1-12)
 * @param minute - Minute string (00-59)
 * @param ampm - AM/PM string
 * @param date - Date string in YYYY-MM-DD format
 * @returns ISO format string (YYYY-MM-DDTHH:mm:ss)
 */
export function convertToISODate(hour: string, minute: string, ampm: string, date: string): string {
	let hourNum = parseInt(hour);
	if (ampm === 'PM' && hourNum !== 12) hourNum += 12;
	if (ampm === 'AM' && hourNum === 12) hourNum = 0;
	const hourStr = hourNum.toString().padStart(2, '0');
	const minuteStr = minute.padStart(2, '0');
	return `${date}T${hourStr}:${minuteStr}:00`;
}

/**
 * Validates if a value is a valid time part
 * @param value - Value to validate
 * @param hourPart - Part of the hour to validate ('hour' or 'minute')
 * @returns True if valid, false otherwise
 */
export function validateTime(
	value: unknown,
	{ hourPart }: { hourPart: 'minute' | 'hour' }
): value is string {
	if (typeof value !== 'string') {
		throw new Error('Value provided should be a string');
	}

	const hourRegex = /^(1[0-2]{1}|(0|)[0-9])$/;
	const minuteRegex = /^[0-5]{0,1}[0-9]$/;

	if (hourPart === 'minute') {
		return minuteRegex.test(value);
	}

	return hourRegex.test(value);
}

/**
 * Validates if a period is a valid time period (AM or PM)
 * @param period - Period to validate
 * @returns True if valid AM/PM, false otherwise
 */
export function validateDatePeriod(period: unknown): period is 'am' | 'pm' {
	return (
		typeof period === 'string' && (period.toLowerCase() === 'am' || period.toLowerCase() === 'pm')
	);
}
